import { useCallback, useMemo, useState } from 'react';
import { companyInvoiceSummaryService as defaultService } from '../services/companyInvoiceSummaryService';
import { useGetCompanyInvoiceSummary } from './useGetCompanyInvoiceSummary';

// ─── Pure Logic ───────────────────────────────────────────────────────────────

// Billing cycle: Tuesday 00:00 UTC (inclusive) → next Tuesday 00:00 UTC (exclusive).
// Backend contract: created_at >= from  AND  created_at < to

const utcOffsetDays = (dateOrIso, n) => {
  const d = new Date(dateOrIso);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n),
  ).toISOString();
};

/**
 * Returns the most recent Tuesday 00:00 UTC (today if today is Tuesday).
 * This is the start of the CURRENT (possibly incomplete) billing week.
 */
const currentBillingTuesdayUtc = () => {
  const now = new Date();
  const daysSinceTue = (now.getUTCDay() - 2 + 7) % 7;
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysSinceTue),
  ).toISOString();
};

/**
 * Returns { fromDate, toDate } for the last completed Tue–Mon billing cycle.
 * from = previous Tuesday 00:00 UTC (inclusive)
 * to   = current Tuesday 00:00 UTC (exclusive — covers through last Monday)
 */
export const getLastCompletedBillingWeek = () => {
  const thisTue = currentBillingTuesdayUtc();
  return {
    fromDate: utcOffsetDays(thisTue, -7),
    toDate: thisTue,
  };
};

export const buildSummaryParams = (userId, fromDate, toDate) => ({
  userId,
  fromDate,
  toDate,
});

export const deriveSummaryState = summary => ({
  hasData: summary !== null,
  totalInvoices: summary?.totalInvoices ?? 0,
  thisWeekInvoices: summary?.thisWeekInvoices ?? 0,
  totalSaleValue: summary?.totalSaleValue ?? 0,
  netPayout: summary?.netPayout ?? 0,
  applicationFee: summary?.applicationFee ?? 0,
  kraveinFee: summary?.kraveinFee ?? 0,
  deliveryFee: summary?.deliveryFee ?? 0,
  kraveinCharges: summary?.kraveinCharges ?? 0,
  creditCardFees: summary?.creditCardFees ?? 0,
  kraveinPayout: summary?.kraveinPayout ?? 0,
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Orchestrating hook for the Invoice Management stat cards.
 *
 * Manages the date range for the summary query. Defaults to the current week
 * (Monday–Sunday). Delegates fetching to useGetCompanyInvoiceSummary.
 * Any date range change triggers a new request automatically.
 *
 * `service` is injected for testability — pass a mock in tests.
 */
export function useCompanyInvoiceSummary(userId, initialDates = {}, service = defaultService) {
  const initialRange = useMemo(() => getLastCompletedBillingWeek(), []);
  const resolvedFrom = initialDates.fromDate ?? initialRange.fromDate;
  const resolvedTo = initialDates.toDate ?? initialRange.toDate;

  // Committed dates — drive the query. Only update on applyDateRange().
  const [fromDate, setFromDate] = useState(resolvedFrom);
  const [toDate, setToDate] = useState(resolvedTo);

  // Pending dates — what the user is editing in the filter bar.
  const [pendingFromDate, setPendingFromDate] = useState(resolvedFrom);
  const [pendingToDate, setPendingToDate] = useState(resolvedTo);

  // Stable reference — only rebuilds when userId or committed dates change.
  // useGetCompanyInvoiceSummary re-fetches whenever this reference changes.
  const params = useMemo(() => buildSummaryParams(userId, fromDate, toDate), [
    userId,
    fromDate,
    toDate,
  ]);

  const { status, summary, error, refresh } = useGetCompanyInvoiceSummary(
    params,
    service,
  );

  const derived = useMemo(() => deriveSummaryState(summary), [summary]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  // Updates pending state only — does NOT trigger a fetch.
  const setDateRange = useCallback((nextFromDate, nextToDate) => {
    setPendingFromDate(nextFromDate);
    setPendingToDate(nextToDate);
  }, []);

  // offsetWeeks: 0 = last completed week, -1 = week before that, etc.
  const setWeekPreset = useCallback(offsetWeeks => {
    const thisTue = currentBillingTuesdayUtc();
    const from = utcOffsetDays(thisTue, (offsetWeeks - 1) * 7);
    const to = utcOffsetDays(thisTue, offsetWeeks * 7);
    setPendingFromDate(from);
    setPendingToDate(to);
  }, []);

  // Commits pending dates → triggers a new summary fetch.
  const applyDateRange = useCallback(() => {
    setFromDate(pendingFromDate);
    setToDate(pendingToDate);
  }, [pendingFromDate, pendingToDate]);

  return {
    status,
    error,
    fromDate,
    toDate,
    pendingFromDate,
    pendingToDate,

    totalInvoices: derived.totalInvoices,
    thisWeekInvoices: derived.thisWeekInvoices,
    totalSaleValue: derived.totalSaleValue,
    netPayout: derived.netPayout,
    applicationFee: derived.applicationFee,
    kraveinFee: derived.kraveinFee,
    deliveryFee: derived.deliveryFee,
    kraveinCharges: derived.kraveinCharges,
    creditCardFees: derived.creditCardFees,
    kraveinPayout: derived.kraveinPayout,
    hasData: derived.hasData,

    setDateRange,
    setWeekPreset,
    applyDateRange,
    refresh,
  };
}
